import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState, useContext } from 'react';

import { ModalContext } from 'app/app';
import { IconedButton } from 'components/IconedButton';
import { Loader, useAPI, ViewContext } from 'components/lib';

// Componentes
import TicketDetails from './components/TicketDetails';
import TicketMessages from './components/TicketMessages';
import TicketIssues from './components/TicketIssues';

// Dados e estilos
import { ticketIssue } from './ticketIssue';
import '../../../css/quillCustom.css';
import 'react-quill/dist/quill.snow.css';

export default function TicketPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const user = useAPI('/api/user');

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const previousPage = searchParams.get("page") || 1;

  async function fetchTicketData() {
    setLoading(true);
    try {
      const res = await axios.get(`/api/tickets/admin/${id}`);
      const ticketResponseData = res.data.data;

      setTicketData(ticketResponseData);

      if (ticketResponseData.ticketIssues) {
        setSelectedIssues(ticketResponseData.ticketIssues);
      }

      if (ticketResponseData.agencyName) {
        checkAndUpdateAgencyId(ticketResponseData);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  async function checkAndUpdateAgencyId(ticketResponseData) {
    try {
      const exactMatchRes = await axios.get(`/api/account/name/${encodeURIComponent(ticketResponseData.agencyName)}`);

      if (exactMatchRes.data.success) {
        const foundAgencyId = exactMatchRes.data.data.id;

        if (String(ticketResponseData.agencyId) !== String(foundAgencyId)) {
          try {
            const formData = new FormData();
            formData.append('agencyId', foundAgencyId);

            const updateResponse = await axios.put(`/api/tickets/${id}`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            if (updateResponse.data.success) {
              setTicketData(prev => ({
                ...prev,
                agencyId: foundAgencyId
              }));
              console.log('Updated ticket with exact agency match');
            }
          } catch (updateErr) {
            console.error('Error updating ticket with new agencyId:', updateErr);
          }
        }
      } else {
        console.error('No exact match found for agency name:', ticketResponseData.agencyName);
      }
    } catch (accountErr) {
      console.error('Error fetching account information:', accountErr);
    }
  }

  useEffect(() => {
    fetchTicketData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleIssueChange = (issue) => {
    setSelectedIssues((prevSelectedIssues) => {
      const issueObject = { value: issue };
      if (prevSelectedIssues.some((i) => i.value === issue)) {
        return prevSelectedIssues.filter((i) => i.value !== issue);
      } else {
        return [...prevSelectedIssues, issueObject];
      }
    });
  };

  const handleNextClick = async () => {
    if (selectedIssues.length === 0) {
      context.notification.show(
        'Please select at least one issue before continuing.',
        'error',
        true,
        'toast',
        'x-circle'
      );
      return;
    } else {
      await axios
        .put(`/api/tickets/${id}`, { ticketIssues: selectedIssues })
        .then(() => {
          context.notification.show(
            'Issues sent successfully!',
            'success',
            true,
            'toast',
            'check-circle'
          );
        })
        .catch((error) => {
          context.notification.show(
            'There was a problem when sending the selected issues.',
            'error',
            true,
            'toast',
            'x-circle'
          );
          console.error('Error sending issues: ', error);
        })
        .finally(() => {
          setModal({
            name: 'ticket_issue_modal',
            data: {
              assignedAgentEmail: ticketData.assignedAgentEmail,
            },
          });
        });
    }
  };

  useEffect(() => {
    if (
      ticketData &&
      (ticketData.ticketIssues !== undefined || null)
    ) {
      if (
        ticketData.ticketIssues.length > 0 &&
        !ticketData.messages
      ) {
        setModal({
          name: 'ticket_issue_modal',
          data: {
            assignedAgentEmail: ticketData.assignedAgentEmail,
          },
        });
      }
    }
  }, [setModal, ticketData]);

  const handleSubmit = async (values) => {
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('sender', 'Cover Badger Support');
      formData.append('receiver', ticketData.assignedAgentEmail);
      formData.append('title', values.title);
      formData.append('description', values.description);
      [...selectedFiles].forEach((file) => {
        formData.append(`documents`, file, file.name);
      });

      const response = await axios.put(
        `/api/tickets/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (response.data.success) {
        context.notification.show(
          'Message sent successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
        setModal('');
      }
    } catch (error) {
      setSending(false);
      context.notification.show(
        error.message ||
        'There was a problem when sending the message.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      fetchTicketData();
      setSending(false);
    }
  };

  const handleUpdateTicketIssue = async (issueId, solved) => {
    try {
      const updatedIssues = ticketData.ticketIssues.map((issue) =>
        issue.value === issueId ? { ...issue, solved } : issue
      );

      const response = await axios.put(`/api/tickets/${id}`, {
        ticketIssues: updatedIssues,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update ticket issue');
      }

      setTicketData((prevData) => ({
        ...prevData,
        ticketIssues: updatedIssues,
      }));

      fetchTicketData();
    } catch (error) {
      context.notification.show(
        'There was a problem when updating the issue.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };

  if (user.loading || loading) return <Loader />;
  if (error) {
    return <div>Error loading ticket data: {error.message}</div>;
  }
  if (!ticketData) {
    return <div>No ticket data available</div>;
  }

  return (
    <>
      <div className="w-full flex items-center justify-left">
        <IconedButton
          size="130px"
          color="#d32425"
          textColor="white"
          icon="arrow-left"
          text="Go back"
          action={() => {
            navigate(`/admin/service_center?page=${previousPage}`);
          }}
        />
      </div>

      {ticketData &&
        (ticketData.ticketIssues.length === 0 ||
          !ticketData.messages) ? (
        <div
          className="mb-6 xl:w-1/2 sm:w-3/4 xs:w-full mx-auto bg-white pb-6 max-w-screen-xl"
          id="14"
        >
          <TicketIssues
            ticketData={ticketData}
            handleIssueChange={handleIssueChange}
            selectedIssues={selectedIssues}
            handleNextClick={handleNextClick}
            ticketIssue={ticketIssue}
          />
        </div>
      ) : (
        <main className="flex-1 flex flex-col md:flex-row gap-4 max-h-full mt-2">
          {/* Left panel  */}
          <TicketDetails
            ticketData={ticketData}
            handleUpdateTicketIssue={handleUpdateTicketIssue}
            ticketIssue={ticketIssue}
            handleIssueChange={handleIssueChange}
            selectedIssues={selectedIssues}
            handleNextClick={handleNextClick}
            setModal={setModal}
          />

          {/* Right panel */}
          <TicketMessages
            ticketData={ticketData}
            handleSubmit={handleSubmit}
            sending={sending}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            setModal={setModal}
          />
        </main>
      )}
    </>
  );
}
